/**
 * MultiSelectFilter - Sprint 4.4 e 4.5 + Melhorias UX
 *
 * Componente reutilizável de filtro multi-select com dropdown
 * Usado em Contagens e Calendário para filtrar por hospital, cliente, auditor e status
 *
 * Features:
 * - Opção "Todos" para selecionar/desselecionar tudo
 * - Botão "Limpar todos" visível no dropdown
 * - Tags dos itens selecionados
 * - Lógica inteligente: auto-seleciona "Todos" quando todos itens estão marcados
 */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check, Search } from 'lucide-react';

export interface FilterOption {
  value: string | number;
  label: string;
  // #11: texto extra para busca (ex.: CNPJ) — alem do label visivel.
  searchValue?: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: FilterOption[];
  selectedValues: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  expanded?: boolean; // Item 9: Controle externo de expansão
  showTodos?: boolean; // Mostrar opção "Todos"
  showBadges?: boolean; // Mostrar badges dos itens selecionados
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = 'Selecione...',
  disabled = false,
  expanded, // Item 9: Prop opcional para controlar expansão
  showTodos = true, // Por padrão mostra opção "Todos"
  showBadges = true, // Por padrão mostra badges
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Item 9: Sincronizar estado local com prop external quando fornecida
  useEffect(() => {
    if (expanded !== undefined) {
      setIsOpen(expanded);
    }
  }, [expanded]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset search term when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Filtrar opções baseado na busca
  const filteredOptions = options.filter(opt => {
    const term = searchTerm.toLowerCase();
    return opt.label.toLowerCase().includes(term) ||
      (opt.searchValue || '').toLowerCase().includes(term);
  });

  // Verifica se todos os itens (filtrados) estão selecionados
  const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(opt => selectedValues.includes(opt.value));

  const toggleTodos = () => {
    if (allFilteredSelected) {
      // Desmarcar todos os visíveis
      const visibleValues = filteredOptions.map(opt => opt.value);
      onChange(selectedValues.filter(v => !visibleValues.includes(v)));
    } else {
      // Marcar todos os visíveis
      const newSelected = [...selectedValues];
      filteredOptions.forEach(opt => {
        if (!newSelected.includes(opt.value)) {
          newSelected.push(opt.value);
        }
      });
      onChange(newSelected);
    }
  };

  const toggleOption = (value: string | number) => {
    const newSelected = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];

    onChange(newSelected);
  };

  const clearAll = () => {
    onChange([]);
  };

  const removeOption = (value: string | number) => {
    onChange(selectedValues.filter(v => v !== value));
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (options.length > 0 && selectedValues.length === options.length && showTodos) return 'Todos selecionados';
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option?.label || placeholder;
    }
    return `${selectedValues.length} ${selectedValues.length === 1 ? 'selecionado' : 'selecionados'}`;
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between transition-colors ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 cursor-not-allowed'
            : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400'
        }`}
      >
        <span className={`text-sm ${selectedValues.length === 0 ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'} truncate mr-2`}>
          {getDisplayText()}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {selectedValues.length > 0 && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  clearAll();
                }
              }}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors cursor-pointer"
              title="Limpar seleção"
            >
              <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        </div>
      </button>

      {/* Badges dos itens selecionados - Mais destacados */}
      {showBadges && selectedValues.length > 0 && !(options.length > 0 && selectedValues.length === options.length) && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedValues.map(value => {
            const option = options.find(opt => opt.value === value);
            if (!option) return null;
            return (
              <span
                key={value}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-sm font-medium rounded-lg border border-blue-200 dark:border-blue-700"
              >
                {option.label}
                <button
                  type="button"
                  onClick={() => removeOption(value)}
                  className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                  disabled={disabled}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden flex flex-col max-h-80">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="overflow-y-auto max-h-60">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                {options.length === 0 ? 'Nenhuma opção disponível' : 'Nenhum resultado encontrado'}
              </div>
            ) : (
              <div className="py-1">
                {/* Opção "Todos" (Aparece apenas se houver filtro ou se for a lista completa) */}
                {showTodos && filteredOptions.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleTodos}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700 ${
                      allFilteredSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <span className={`font-bold ${allFilteredSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                      {searchTerm ? 'Selecionar filtrados' : 'Todos'}
                    </span>
                    {allFilteredSelected && (
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    )}
                  </button>
                )}

                {/* Opções individuais */}
                {filteredOptions.map(option => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => toggleOption(option.value)}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                        isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <span className={isSelected ? 'font-medium text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}>
                        {option.label}
                      </span>
                      {isSelected && (
                        <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
